import { Box, Theme, Tooltip, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { FC, useMemo, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useGetDeviations } from 'src/api/deviation'
import { AppRoutesNavigation } from 'src/app-routes'
import AccordionCommon from 'src/components/AccordionCommon'
import { Chip } from 'src/components/common/Chip'
import { CaptionedCell } from 'src/components/table/CaptionedCell'
import GenericTable from 'src/components/table/GenericTable'
import { useNavigation } from 'src/context/NavigationContext'
import { IDeviation } from 'src/generated-types/deviation'
import { IDeviationPriority } from 'src/generated-types/deviation-priority'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { APIResultCount } from 'src/constant'

interface ExistingDeviationsAccordionProps {
  customerId: EntityId
  localityId?: EntityId
  mainComponentId?: EntityId
  componentId?: EntityId
}

const MAX_AUTO_HEIGHT_ROWS = 4
const SCROLLABLE_TABLE_HEIGHT = 240

const renderClampedText = (text: string) => (
  <Tooltip title={text} placement="top-start" enterDelay={300}>
    <Box minWidth={0}>
      <CaptionedCell primary={text} sx={{ fontWeight: 500 }} />
    </Box>
  </Tooltip>
)

const getColumns = (t: TFunction<('deviation' | 'common')[]>, theme: Theme): GridColumns<IDeviation> => [
  {
    field: 'id',
    headerName: t('id', { ns: 'common' }),
    width: 60,
  },
  {
    field: 'title',
    headerName: t('title', { ns: 'common' }),
    flex: 1,
    renderCell: ({ row }) => renderClampedText(row.title),
  },
  {
    field: 'description',
    headerName: t('description', { ns: 'common' }),
    flex: 1,
    renderCell: ({ row }) => renderClampedText(row.description),
  },
  {
    field: 'createdAt',
    headerName: t('registration-date', { ns: 'deviation' }),
    flex: 0.6,
    valueGetter: value => {
      return getFormattedDate(new Date(value.value))
    },
  },
  {
    field: 'dueDate',
    headerName: t('dueDate', { ns: 'common' }),
    flex: 0.6,
    valueGetter: value => {
      return getFormattedDate(new Date(value.value))
    },
  },
  {
    field: 'priority',
    headerName: t('priority', { ns: 'common' }),
    minWidth: 110,
    renderCell: ({ row }) =>
      row.priority === IDeviationPriority.Critical ? (
        <Chip
          size="small"
          label={t('critical', { ns: 'common' })}
          sx={{
            backgroundColor: getThemeColor(theme, MUIThemeColors.errorMain),
            color: `${getThemeColor(theme, MUIThemeColors.white)} !important`,
          }}
        />
      ) : (
        <Chip
          size="small"
          label={t('normal', { ns: 'common' })}
          variant="outlined"
          sx={{ borderColor: getThemeColor(theme, MUIThemeColors.secondaryLight) }}
        />
      ),
  }
]

export const ExistingDeviationsAccordion: FC<ExistingDeviationsAccordionProps> = ({
  customerId,
  localityId,
  mainComponentId,
  componentId,
}) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()
  const { navigateTo } = useNavigation()
  const { closeAllDialogs } = useDialogCtx()

  const { data, isLoading } = useGetDeviations(
    customerId,
    undefined,
    undefined,
    undefined,
    componentId,
    mainComponentId,
    undefined,
    undefined,
    undefined,
    0,
    APIResultCount.More,
    { status: false, priority: undefined },
  )

  const columns = useMemo(() => getColumns(t, theme), [t, theme])
  const [expanded, setExpanded] = useState(false)

  if (isLoading || (data?.items ?? []).length === 0 || (!mainComponentId && !componentId)) return null

  // Grow with the rows up to the cap, then scroll inside a fixed height. headerHeight={35} matches the header
  // GenericTable draws; MUI's 56px default would leave a 21px blank strip under the rows at the fixed height.
  const enableAutoHeight = (data?.items ?? []).length <= MAX_AUTO_HEIGHT_ROWS

  const onRowClick = ({ row }: { row: IDeviation }) => {
    if (!localityId) return
    closeAllDialogs()
    navigateTo(AppRoutesNavigation.Localities.deviation(customerId, localityId, row.id))
  }

  return (
    <Box sx={{ ml: '16px', mb: 2 }}>
      <AccordionCommon
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        removeBoxShadow
        border={`1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
        borderRadius="4px"
        expandedTopMargin="0px"
        expandedBottomMargin="0px"
        title={
          <Flex.Row sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography fontWeight={600}>{t('existing-deviations', { ns: 'deviation' })}</Typography>
            <Chip
              size="small"
              label={`${data?.items?.length ?? 0} ${t('open', { ns: 'common' })}`}
              sx={{
                backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
                color: `${getThemeColor(theme, MUIThemeColors.primaryDark)} !important`,
                height: '24px',
              }}
            />
          </Flex.Row>
        }
      >
        <Box sx={{ maxHeight: '240px' }}>
          <GenericTable
            id="existing-deviations-accordion-table"
            columns={columns}
            data={data?.items ?? []}
            getRowHeight={() => 'auto'}
            hideDefaultFooterPagination
            enablePagination={false}
            autoHeight={enableAutoHeight}
            height={enableAutoHeight ? 'auto' : SCROLLABLE_TABLE_HEIGHT}
            headerHeight={35}
            onRowClick={onRowClick}
            getRowClassName={() => (localityId ? 'hover-pointer' : '')}
          />
        </Box>
      </AccordionCommon>
    </Box>
  )
}
